/* eslint @typescript-eslint/no-unnecessary-condition: 0 */
import {
    ChangeDetectorRef,
    DestroyRef,
    Directive,
    ElementRef,
    Input,
    OnChanges,
    OnInit,
    QueryList,
    Signal,
    SimpleChanges,
    ViewChildren,
    WritableSignal,
    computed,
    inject,
    signal,
} from '@angular/core';
import { Router } from '@angular/router';

import { EnvironmentConfig } from '../../../client-config/index';
import { PubSubService } from '../../../core/services/communication/pubsub/pubsub.service';
import { DeviceService } from '../../../core/services/device/device.service';
import { FracToDecService } from '../../../core/services/fracToDec/frac-to-dec.service';
import { GtmService } from '../../../core/services/gtm/gtm.service';
import { IGtmBetPosition } from '../../../core/services/gtmTracking/models/gtm-origin.model';
import { RoutingHelperService } from '../../../core/services/routingHelper/routing-helper.service';
import { TimeService } from '../../../core/services/time/time.service';
import { WindowRefService } from '../../../core/services/windowRef/window-ref.service';
import { Carousel } from '../ng-carousel/carousel.class';
import { CarouselService } from '../ng-carousel/carousel.service';
import { pbbConstants } from './precreated-betbuilder.constant';
import {
    Event,
    GA_CONSTANTS,
    IGtmData,
    IPbbData,
    IfeaturedPreCreatedBetBuilder,
    PrcrCombo,
    PreCreatedBetBuilderData,
} from './precreated-betbuilder.model';

@Directive({
    standalone: true,
    providers: [CarouselService, GtmService],
})

/**
 * This component is responsible for creating Pre created bet builder widget for precreated bet builder data
 */
export class PreCreatedBetBuilder implements OnInit, OnChanges {
    slides: WritableSignal<HTMLElement[]> = signal<HTMLElement[]>([]);
    @ViewChildren('slide') slideRefs: QueryList<ElementRef<HTMLElement>>;

    pbbSections: WritableSignal<HTMLElement[]> = signal<HTMLElement[]>([]);
    @ViewChildren('sectionPbb') pbbSectionRefs: QueryList<ElementRef<HTMLElement>>;

    protected destroyRef: DestroyRef = inject(DestroyRef);

    sizeAccessor: ({ borderBoxSize }: ResizeObserverEntry) => { width: number; height: number } = ({ borderBoxSize }: ResizeObserverEntry) => {
        const { inlineSize, blockSize } = borderBoxSize[0]!;
        return { width: inlineSize, height: blockSize };
    };

    pbbHeaderHeight: WritableSignal<number> = signal(0);
    oddsCardHeight: WritableSignal<number> = signal(0);
    slideWidth: WritableSignal<number> = signal(0);
    carouselWidth: WritableSignal<number> = signal(0);
    pbbWidgetScroll: string = 'PbbWidget';

    @Input() module: IfeaturedPreCreatedBetBuilder;
    @Input() sportName: string | number = '0';
    @Input() isV2Design?: boolean;
    @Input() eventRef: Event[];

    pbbWidgetData: WritableSignal<PreCreatedBetBuilderData[]> = signal<PreCreatedBetBuilderData[]>([]);
    PbbCarousel: string = pbbConstants.PBBWIDGETCAROUSEL;
    featuredModule: string = pbbConstants.FEATURED_MODULE;

    expanded: boolean = true;

    showCarouselButtons: Signal<boolean> = computed(() => {
        const carouselSlides = this.pbbWidgetData().length;
        const carouselOuterWidth = this.carouselWidth();
        const slideWidth = this.slideWidth();
        const allSlidesWidth = carouselSlides * slideWidth;
        const minSlidesOffset = pbbConstants.MINSLIDESEFFECT; // Arrows appear when there is minimum 2 slides on carousel. One slide has 10px of margin.
        const fit = carouselOuterWidth >= allSlidesWidth + minSlidesOffset;
        this.disableScroll = this.isFit = fit;
        return carouselOuterWidth <= allSlidesWidth;
    });

    isFit: boolean;
    disableScroll: boolean;
    pbbIndex: number = pbbConstants.PBBSTATICINDEX;
    pbbWidget: string = pbbConstants.PBBWIDGET;
    pbbCmsData: WritableSignal<IfeaturedPreCreatedBetBuilder> = signal<IfeaturedPreCreatedBetBuilder>(null!);
    isDesktop: boolean;
    isLads: boolean = false;
    private readonly environment = inject(EnvironmentConfig).configuration;
    showLoader: boolean;
    position: number;
    locationEvent: string;
    scrollType: string;
    sportSegment: string; // Variable used for GA tracking
    isFeatured: boolean;
    accordionSvg: string = '';
    precreatedBetBuilder: string = 'precreatedBetBuilder';
    deviceService = inject(DeviceService);
    pubsub = inject(PubSubService);
    fracToDecService = inject(FracToDecService);
    gtmService = inject(GtmService);
    windowRef = inject(WindowRefService);
    router = inject(Router);
    carouselService = inject(CarouselService);
    routingSvc = inject(RoutingHelperService);
    timeService = inject(TimeService);

    /**
     * Constructor initialisation for precreated bet builder class
     * @param timeService
     * @param routingSvc
     * @param carouselService
     * @param windowRef
     * @param router
     * @param fracToDecService
     */
    constructor(private changeDet: ChangeDetectorRef) {}

    /**
     * On init life cycle hook for Pre created bet builder
     */
    ngOnInit(): void {
        this.setPbbModuleData();
    }

    /**
     * Remove all the pipes for the event name
     * @param text
     * @returns string
     */
    removePipes(text: string): string {
        if (text) {
            return text.replace(/\|/g, '').trim();
        }
        return '';
    }

    /**
     * Set precreated Modules data to the carousel component
     */
    setPbbModuleData(): void {
        if (this.sportName === pbbConstants.HOME_SPORT) {
            this.isFeatured = true;
        }
        this.isLads = this.environment.BRAND === pbbConstants.LADS_BRAND;
        this.accordionSvg = this.isLads ? pbbConstants.FOOTBALL2025 : '';
        this.showLoader = true;

        this.locationEvent = this.module?.sportName || (this.module?.sportId === 0 ? pbbConstants.LOCATION_HOME : pbbConstants.LOCATION_EDP);
        this.isDesktop = this.deviceService.isDesktop;
        this.pbbCmsData.set(this.module);
        const [data] = (this.module?.data ?? []) as PreCreatedBetBuilderData[];
        if (data?.prcrComboResponse?.prcrCombos) {
            this.pbbWidgetData.set(data.prcrComboResponse.prcrCombos);
        }

        this.pbbIndex = this.pbbIndex || 0;
        this.PbbCarousel = pbbConstants.PBBWIDGETCAROUSEL;
        this.getSportSegment();
    }

    /**
     * on changes fetch and reassign data for precreated bet builder data
     * @param changes
     * @returns void
     */
    ngOnChanges(changes: SimpleChanges): void {
        if (changes && changes?.module && changes?.module.currentValue && !changes.module.firstChange) {
            this.module = changes.module.currentValue;
            this.setPbbModuleData();
            this.changeDet.detectChanges();
        }
    }

    /**
     *
     * @param eventEntity
     * @returns string
     */
    goToMrktOrEdp(eventEntity: PrcrCombo, cardPosition: number): string {
        if (eventEntity) {
            const [events]: Event[] = eventEntity?.events;
            const edpUrlData = {
                categoryId: events?.categoryId,
                categoryName: events?.categoryName,
                className: events?.className,
                typeName: events?.typeName,
                name: events?.name,
                id: events?.id,
            };
            const url: string = this.routingSvc.formEdpUrl(edpUrlData);
            const suffix: string = pbbConstants.BETBUILDER;
            const fullUrl = `${url}/${suffix}`;

            this.router.navigateByUrl(fullUrl).then((navigationStat) => {
                if (!navigationStat) {
                    this.router.navigateByUrl(url);
                }
            });
            const arg: {
                positionEvent: number;
                eventDetails: string;
                url: string;
            } = {
                positionEvent: cardPosition,
                eventDetails: this.removePipes(events?.name) ?? '',
                url: `${this.windowRef.nativeWindow.location.href}${url}`,
            };
            this.gaTrackingRequest(this.sportSegment, 'click', arg.url, arg);
            return `${url}${suffix}`;
        }
        return '';
    }

    /**
     * Format eventName based on virtual an selection time of an event
     * @param event
     * @returns string
     */
    getEventName(pbbData: IPbbData): string {
        const [events] = pbbData?.events;
        return events?.startTime ? `${this.getSelectionTime(events?.startTime)} - ${this.removePipes(events?.typeName)}` : events?.name;
    }

    /**
     * Format the date value based on today or tomorrow
     * @param date
     * @returns date in day, hrs. and min. format
     */
    getSelectionTime(dateValue: string): string {
        const day: string = this.timeService.determineDay(dateValue, false);
        if (day) {
            const [dayFirstEle]: string = day;
            const todayOrTomorrow: boolean = ['today', 'tomorrow'].includes(day);
            const format: string = todayOrTomorrow ? 'HH:mm' : 'dd MMM, HH:mm';
            const date = this.timeService['datePipe'].transform(dateValue, format) || '';
            const ucaseDay: string = todayOrTomorrow ? dayFirstEle.toUpperCase() + day.slice(1) : '';
            return todayOrTomorrow ? `${ucaseDay}, ${date}` : date;
        }
        return '';
    }

    /**
     * carousel next button
     */
    nextSlide(): void {
        this.gaTrackingRequest(this.sportSegment, GA_CONSTANTS.LEFT);
        this.carousel?.next();
    }

    /**
     * carousel previous button
     */
    prevSlide(): void {
        this.gaTrackingRequest(this.sportSegment, GA_CONSTANTS.RIGHT);
        this.carousel?.previous();
    }

    /**
     * carousel data
     * @returns Carousel
     */
    private get carousel(): Carousel | null {
        return this.carouselService.get(this.PbbCarousel);
    }

    /**
     * carousel set data
     */
    private set carousel(value: Carousel) {}

    /**
     * carousel get next
     * @returns boolean
     */
    get showNext(): boolean {
        return !!this.carousel && this.carousel.currentSlide < this.carousel.slidesCount - 1;
    }

    /**
     * carousel set next
     */
    set showNext(value: boolean) {}

    /**
     * carousel get prev
     */
    get showPrev(): boolean {
        return !!this.carousel && this.carousel.currentSlide > 0;
    }

    /**
     * carousel set prev
     */
    set showPrev(value: boolean) {}

    /**
     * GA tracking object for slides scroll
     * @param event
     */
    fetchPbbWidgetCarouselData(event: string): void {
        if (event && !this.isDesktop && this.scrollType !== event) {
            this.gaTrackingRequest(this.sportSegment, event === GA_CONSTANTS.NEXT ? GA_CONSTANTS.LEFT : GA_CONSTANTS.RIGHT);
            this.scrollType = event;
        }
    }

    /**
     * Get dimenstion 180 for Ga tracking
     * @param eventEntity
     * @param selectedCardInd
     * @param cardPosition
     * @returns IGtmBetPosition
     */
    getDimension180(eventEntity: any, selectedCardInd: any, cardPosition: any): IGtmBetPosition {
        const currentPage = eventEntity?.pager?.currentPage;
        return {
            position: `${this.sportSegment}-${cardPosition}cc-${currentPage}pc-${selectedCardInd}bp-${eventEntity && eventEntity.name}`,
            subTab: pbbConstants.SUBTAB,
            personalised: '',
        };
    }

    /**
     * manage the state of the accordion
     * @param isExpanded
     */
    manageAccordionClick(isExpanded: boolean): void {
        this.expanded = isExpanded;
        this.gaTrackingRequest(this.sportSegment, this.expanded ? GA_CONSTANTS.EXPAND : GA_CONSTANTS.COLLAPSE);
    }

    /**
     * GA trackng for naviagiton of slides
     */
    gaTrackingRequest(
        locationEvent: string,
        action: string,
        urlClicked?: string,
        hrefDetails?: { positionEvent: string | number; eventDetails: string },
    ): void {
        const gtmdata: IGtmData = {
            event: GA_CONSTANTS.TRACKING,
            data: {
                'component.CategoryEvent': GA_CONSTANTS.WIDGET,
                'component.LabelEvent': GA_CONSTANTS.PBB,
                'component.ActionEvent': action,
                'component.PositionEvent': hrefDetails ? hrefDetails.positionEvent : GA_CONSTANTS.NOT_APPLICABLE,
                'component.LocationEvent': locationEvent,
                'component.EventDetails': hrefDetails ? hrefDetails.eventDetails : GA_CONSTANTS.PBB_WIDGET,
                'component.URLClicked': urlClicked ? urlClicked : GA_CONSTANTS.NOT_APPLICABLE,
            },
        };
        this.gtmService.push(gtmdata.event, gtmdata.data);
    }

    /**
     * it should return segment location ex: sport/football,home etc..
     * @returns void
     */
    getSportSegment(): void {
        const sportNameStr = this.sportName.toString();
        this.sportSegment = pbbConstants.SPORT_SEGMENT[sportNameStr as keyof typeof pbbConstants.SPORT_SEGMENT] || '';
    }
}
